import type { ThirdPartyConfigsWithAPIResponse } from '@atlassian/rovo-connectors-config/schemas/types';

import type { LogoKey } from '../ProductLogos.tsx';

export type SuggestionProductKey =
	| 'googleDocs'
	| 'googleSheets'
	| 'googleSlides'
	| 'googleDrive'
	| 'loom'
	| 'microsoftTeams'
	| 'link';

export enum Action {
	SUGGEST = 'SUGGEST',
	ACCEPT = 'ACCEPT',
	REJECT = 'REJECT',
	IGNORE = 'IGNORE',
}

export const EXTERNAL_PRODUCT_CONNECTION = {
	LOOM: 'loom',
	GOOGLE_DRIVE: 'drive',
	MICROSOFT: 'microsoft',
} as const;

export type ExternalProductConnection =
	(typeof EXTERNAL_PRODUCT_CONNECTION)[keyof typeof EXTERNAL_PRODUCT_CONNECTION];

export type AuthenticationStatus = {
	isAuthenticated: boolean;
	outboundAuthUrl?: string;
	error?: Error;
	icon?: ThirdPartyConfigsWithAPIResponse['icon'];
};

export const ALL_EXTERNAL_PRODUCT_CONNECTIONS: ExternalProductConnection[] = Object.values(
	EXTERNAL_PRODUCT_CONNECTION,
);

const PROVIDER_INTEGRATION_TO_CONNECTION: Record<string, ExternalProductConnection> = {
	google: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	'google-drive': EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	loom: EXTERNAL_PRODUCT_CONNECTION.LOOM,
	microsoft: EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
	'microsoft-teams': EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
};

export const getConnectionForProviderId = (
	providerId: string,
): ExternalProductConnection | undefined => {
	const integration = providerId.split('integration/')[1]?.split('/')[0];
	return integration ? PROVIDER_INTEGRATION_TO_CONNECTION[integration] : undefined;
};

export const AUTH_OPTIONS: readonly ['drive', 'microsoft'] = [
	EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
] as const;

export type SocialSignal = {
	totalVisits?: number;
	lastVisited?: string;
	ownerIsSpaceContributor?: boolean;
};

export type Suggestion = {
	id: string;
	title: string;
	description?: string;
	explanation?: string;
	socialSignal?: SocialSignal;
	relatedPages?: string[];
	url: string;
	parentId: number | null;
	action: Action;
	fallbackParents?: number[];
	iconUrl?: string;
	creatorAccountId?: string;
	providerId?: string;
	score?: number;
};

export type SuggestionGroup = {
	parentId: number | null;
	remoteObjects: Suggestion[];
};

const PRODUCT_ICON_MAP: Array<{
	pattern: string;
	product: string;
	productKey: Exclude<SuggestionProductKey, 'link'>;
	logoKey: LogoKey;
	connection: ExternalProductConnection;
}> = [
	{
		pattern: 'docs.google.com/document',
		product: 'Google Docs',
		productKey: 'googleDocs',
		logoKey: 'google-docs',
		connection: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	},
	{
		pattern: 'docs.google.com/spreadsheets',
		product: 'Google Sheets',
		productKey: 'googleSheets',
		logoKey: 'google-sheets',
		connection: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	},
	{
		pattern: 'docs.google.com/presentation',
		product: 'Google Slides',
		productKey: 'googleSlides',
		logoKey: 'google-slides',
		connection: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	},
	{
		pattern: 'drive.google.com',
		product: 'Google Drive',
		productKey: 'googleDrive',
		logoKey: 'google-drive',
		connection: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
	},
	{
		pattern: 'loom.com',
		product: 'Loom',
		productKey: 'loom',
		logoKey: 'loom',
		connection: EXTERNAL_PRODUCT_CONNECTION.LOOM,
	},
	{
		pattern: 'teams.microsoft.com',
		product: 'Microsoft Teams',
		productKey: 'microsoftTeams',
		logoKey: 'microsoft-teams',
		connection: EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
	},
	{
		pattern: 'sharepoint.com',
		product: 'Microsoft SharePoint',
		productKey: 'microsoftTeams',
		logoKey: 'microsoft-sharepoint',
		connection: EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
	},
];

function getProductIconMapEntryForContentUrl(url: string) {
	return PRODUCT_ICON_MAP.find((entry) => url.includes(entry.pattern));
}

export function getSuggestionProductKeyFromContentUrl(url: string): SuggestionProductKey {
	return getProductIconMapEntryForContentUrl(url)?.productKey ?? 'link';
}

export function getSuggestionLogoKeyFromContentUrl(url: string): LogoKey | undefined {
	return getProductIconMapEntryForContentUrl(url)?.logoKey;
}

export function getConnectionFromContentUrl(url: string): ExternalProductConnection | undefined {
	return getProductIconMapEntryForContentUrl(url)?.connection;
}
